import { useState, useEffect, useCallback } from "react";
import { useAdxQuery } from "../lib/useAdxQuery";
import { getAnalytics } from "../lib/apiClient";
import { RANGES } from "../lib/format";
import PageHead from "../components/PageHead";
import { Icon } from "../components/Icon";
import { ErrorAlert, Loading } from "../components/States";

function BarChart({ title, rows, labelKey, valueKey, formatValue }) {
  const data = rows || [];
  const max = Math.max(0, ...data.map(r => r[valueKey] || 0));
  return (
    <section className="card">
      <div className="card__head"><h2 className="card__title">{title}</h2></div>
      <div className="card__body">
        {data.length === 0 ? (
          <p className="muted chart__empty">No data for this period.</p>
        ) : (
          <div className="bars">
            {data.map((r, i) => {
              const val = r[valueKey] || 0;
              const pct = max > 0 ? Math.round((val / max) * 100) : 0;
              const label = r[labelKey] || "—";
              const shortLabel = label.includes("@") ? label.split("@")[0] : label.replace("claude_", "");
              const display = formatValue ? formatValue(val) : Number(val).toLocaleString();
              return (
                <div className="bar" key={r[labelKey] ?? i}>
                  <span className="bar__label" title={label}>{shortLabel}</span>
                  <span className="bar__track"><span className="bar__fill" style={{ width: `${Math.max(pct, 2)}%` }} /></span>
                  <span className="bar__value">{display}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export default function AnalyticsView() {
  const { run, loading, error } = useAdxQuery();
  const [data, setData] = useState(null);
  const [days, setDays] = useState("7");

  const load = useCallback(async () => {
    const result = await run(getAnalytics, parseInt(days));
    if (result) setData(result);
  }, [run, days]);

  useEffect(() => { load(); }, [days]); // eslint-disable-line

  const totalCost = data?.costByUser?.length
    ? `$${data.costByUser.reduce((s, r) => s + (r.TotalCost || 0), 0).toFixed(2)}`
    : "—";

  const kpis = data ? [
    { label: "Activity events", value: data.activityCount?.toLocaleString() ?? "—", sub: "ComplianceActivities" },
    { label: "Prompts logged", value: data.promptCount?.toLocaleString() ?? "—", sub: "OTELLogs" },
    { label: "Active users", value: data.activeUsers ?? "—", sub: "With activity" },
    { label: "Est. cost", value: totalCost, sub: "Claude Code OpenTelemetry" },
  ] : [];

  return (
    <>
      <PageHead
        viewId="analytics"
        description="Activity, prompt volume, and estimated Claude Code cost for the selected period."
        actions={
          <>
            <label className="sr-only" htmlFor="analytics-range">Time range</label>
            <select id="analytics-range" className="filterfield" value={days} onChange={e => setDays(e.target.value)}>
              {RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <button className="btn btn--secondary" type="button" onClick={load} disabled={loading}>
              <Icon name="refresh" size={16} /> Refresh
            </button>
          </>
        }
      />
      <div className="content">
        <ErrorAlert message={error} />
        {loading || (!data && !error) ? <Loading label="Loading analytics…" /> : data && (
          <>
            <div className="cardrow">
              {kpis.map(k => (
                <div className="card kpi" key={k.label}>
                  <div className="kpi__label">{k.label}</div>
                  <div className="kpi__value">{k.value}</div>
                  <div className="kpi__delta muted">{k.sub}</div>
                </div>
              ))}
            </div>
            <div className="split">
              <BarChart title="Activity by user" rows={data.userBreakdown} labelKey="ActorEmail" valueKey="Count" />
              <BarChart title="Events by type" rows={data.eventBreakdown} labelKey="EventType" valueKey="Count" />
            </div>
            <BarChart
              title="Claude Code cost by user"
              rows={data.costByUser}
              labelKey="UserEmail"
              valueKey="TotalCost"
              formatValue={v => `$${Number(v).toFixed(4)}`}
            />
          </>
        )}
      </div>
    </>
  );
}
